import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LogService } from './log.service';
import { MessagesService } from './messages.service';
import { Observable } from 'rxjs/Observable';
import { Vehicle } from '../models/vehicles/vehicle';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { VehicleFilter } from '../models/vehicles/vehicleFilter';
import { CommonService } from './common.service';
import { PageOption } from '../models/common/pageOption';
import { KeyValuePair } from '../models/common/keyValuePair';
import { CACHE_KEYS } from '../contants/CACHE_KEYS';
import { CacheService } from './cache.service';
import { of } from 'rxjs/observable/of';
import { RequestResult } from '../models/common/requestResult';
import { PageOptionResult } from '../models/common/pageOptionResult';
import { VehicleIncomeReportFilter } from '../models/vehicles/vehicle.income.report.filter';
import { VehicleIncomeReport } from '../models/vehicles/vehicle.income.report';

@Injectable()
export class VehicleService {
	private origin: string = environment.API_BASE_URL;

	constructor(
		private _http: HttpClient,
		private _logService: LogService,
		private _messagesService: MessagesService,
		private _common: CommonService,
		private _cacheService: CacheService
	) { }

	getVehicle(id: string): Observable<Vehicle> {
		return this._http.get<Vehicle>(`${this.origin}/api/vehicles/${id}`)
			.pipe(
				tap(x => this.log(`getVehicle: id=${id}`)),
				catchError(this._logService.handleError<Vehicle>('getVehicle'))
			);
	}

	getVehiclesList(filter: PageOption<VehicleFilter>): Observable<PageOptionResult<Vehicle>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PageOptionResult<Vehicle>>(`${this.origin}/api/vehicles${param}`)
			.pipe(
				tap(x => this.log(`getVehiclesList param = ${param}`)),
				catchError(this._logService.handleError<PageOptionResult<Vehicle>>('getVehiclesList'))
			);
	}

	getVehiclesDropdownList(): Observable<KeyValuePair[]> {
		let value = this._cacheService.get(CACHE_KEYS.vehicleSelection)
		if (value != null) {
			return of(value);
		}

		return this._http.get<KeyValuePair[]>(`${this.origin}/api/menus/vehicles`)
			.pipe(
				tap(x => {
					this.log(`getVehiclesDropdownList`);
					this._cacheService.add(CACHE_KEYS.vehicleSelection, x, 240);
				}),
				catchError(this._logService.handleError<KeyValuePair[]>('getVehiclesDropdownList'))
			);
    }
    
    getReport(filter: VehicleIncomeReportFilter): Observable<VehicleIncomeReport> {
		var queryString = this._common.objectToQueryParam(filter);
        return this._http.get<VehicleIncomeReport>(`${this.origin}/api/vehicles/reports${queryString}`,)
            .pipe(
                tap(x => {
                    this.log(`getReport`);
                }),
                catchError(this._logService.handleError<VehicleIncomeReport>('getReport'))
            );;
    }

    exportReport(filter: VehicleIncomeReportFilter) {
		var queryString = this._common.objectToQueryParam(filter);
		return this._http.get(`${this.origin}/api/vehicles/reports/export${queryString}export=true`, {responseType: 'arraybuffer'});
    }

	addVehicle(vehicle: Vehicle): Observable<RequestResult<Vehicle>> {
		this._cacheService.remove(CACHE_KEYS.vehicleSelection);
		return this._http.post<RequestResult<Vehicle>>(`${this.origin}/api/vehicles`, JSON.stringify(vehicle))
			.pipe(
				tap(x => this.log(`addVehicle: id=${vehicle.plateNumber}`)),
				catchError(this._logService.handleError<RequestResult<Vehicle>>('addVehicle'))
			);
	}

	updateVehicle(vehicle: Vehicle): Observable<RequestResult<Vehicle>> {
		this._cacheService.remove(CACHE_KEYS.vehicleSelection);
		return this._http.put<RequestResult<Vehicle>>(`${this.origin}/api/vehicles/${vehicle.id}`, JSON.stringify(vehicle))
			.pipe(
				tap(x => this.log(`updateVehicle: id=${vehicle.id}`)),
				catchError(this._logService.handleError<RequestResult<Vehicle>>('updateVehicle'))
			);
	}

	copy(source: Vehicle): Vehicle {
		if(source === undefined || source === null){
			return null;
		}
		let copy = new Vehicle();
		copy.id = source.id;
		copy.plateNumber = source.plateNumber;
		copy.isTruck = source.isTruck;
		copy.createdBy = source.createdBy;
		copy.createdDate = source.createdDate;
		copy.updatedBy = source.updatedBy;
		copy.updatedDate = source.updatedDate;
		return copy;
	}

	private log(message: string) {
		this._messagesService.add('VehicleService: ' + message);
	}
}



// WEBPACK FOOTER //
// ./src/app/services/vehicle.service.ts